import React, { useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ScreenQuad } from '@react-three/drei';
import * as THREE from 'three';

import { cn } from '@/lib/utils';

/**
 * The brand at the centre of its own sphere, as a lit, turning ball.
 *
 * The rest of the orbit is deliberately DOM — real text, real images, real
 * focus. This one piece is not, because it cannot be: an image masked into a
 * circle is a picture of a sphere, and no arrangement of gradients and blurs
 * turns into one that rotates. Lobes of colour have to sweep round a surface
 * and disappear over its edge, and that is a normal-per-pixel job.
 *
 * It is one small transparent canvas with no geometry to speak of — a single
 * full-quad fragment shader — so the cost is a WebGL context, not a scene.
 *
 * The colours are the vendor's own. A generic aurora would have been prettier
 * and meant nothing; a ball made of their palette is the brand kit saying what
 * it knows in the one language that needs no label.
 */

/** How many lobes the shader carries. Fewer colours are repeated to fill it. */
const LOBES = 4;

/**
 * When the kit has no palette yet.
 *
 * Deliberately cool and unbranded: this is the sphere saying "nothing has been
 * read", and picking a warm, confident set here would dress an empty kit up as
 * a finished one.
 */
const UNREAD = ['#8ea3c4', '#a9b6cf', '#c3c9d8', '#7f92b5'];

/*
 * Clip space straight through, and passed on as-is.
 *
 * Not `uv`: drei's ScreenQuad is a single full-screen triangle carrying a
 * `position` attribute and nothing else. Three declares `uv` for any
 * ShaderMaterial whether the geometry has one or not, so reading it compiles
 * cleanly and hands back zero for every vertex — which put every pixel at a
 * radius of √2 and discarded the entire quad. A blank canvas with no error.
 *
 * The triangle's own vertices are already in clip space, so `position.xy`
 * interpolates to −1…1 across the viewport: exactly the coordinate the sphere
 * needs, and one attribute closer to the metal than a uv would have been.
 */
const VERTEX = /* glsl */ `
    varying vec2 vPos;
    void main() {
        vPos = position.xy;
        gl_Position = vec4(position.xy, 0.0, 1.0);
    }
`;

/*
 * A sphere impostor.
 *
 * The quad is flat; the roundness is arithmetic. For each pixel inside the
 * unit circle the surface normal is (x, y, sqrt(1 - x² - y²)), which is the
 * exact normal of a sphere seen head on — so lighting and lobe placement are
 * true rather than faked, and the silhouette is perfectly round at any size
 * with no geometry to tessellate.
 *
 * The lobe directions drift on their own sine cycles at prime-ish rates, so
 * the pattern never visibly repeats. Multiplying the normal by the rotation
 * (rather than moving the lobes) is what makes colour travel over the surface
 * and vanish round the back instead of sliding across a disc.
 */
const FRAGMENT = /* glsl */ `
    precision highp float;

    varying vec2 vPos;
    uniform float uTime;
    uniform vec3 uColors[${LOBES}];
    uniform float uAlpha;
    /** width / height. Keeps the ball round if the canvas ever is not square. */
    uniform float uAspect;

    mat3 rotY(float a) {
        float c = cos(a); float s = sin(a);
        return mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c);
    }
    mat3 rotX(float a) {
        float c = cos(a); float s = sin(a);
        return mat3(1.0, 0.0, 0.0, 0.0, c, s, 0.0, -s, c);
    }

    void main() {
        vec2 p = vPos;
        if (uAspect > 1.0) p.x *= uAspect; else p.y /= max(uAspect, 0.0001);
        float r = length(p);
        if (r > 1.0) discard;

        float z = sqrt(max(0.0, 1.0 - r * r));
        vec3 n = vec3(p, z);

        // Turning, with a slow nod so it never looks like a spinning decal.
        vec3 rn = rotX(sin(uTime * 0.13) * 0.30) * rotY(uTime * 0.32) * n;

        vec3 col = vec3(0.0);
        float weight = 0.0;
        for (int i = 0; i < ${LOBES}; i++) {
            float fi = float(i);
            vec3 dir = normalize(vec3(
                sin(uTime * 0.29 + fi * 2.1),
                cos(uTime * 0.21 + fi * 1.7),
                sin(uTime * 0.17 + fi * 3.3)
            ));
            // A tight exponent keeps each colour a defined lobe; a loose one
            // greys the whole ball as the four of them average together.
            float w = pow(max(dot(rn, dir), 0.0), 2.4);
            col += uColors[i] * w;
            weight += w;
        }
        col = weight > 0.001 ? col / weight : uColors[0];

        // Darker towards the silhouette, which is most of what makes a flat
        // circle read as a ball.
        col *= mix(0.30, 1.12, z);

        // One specular, up and to the left, matching where the page's own
        // light comes from.
        float spec = pow(max(dot(rn, normalize(vec3(-0.4, 0.55, 0.75))), 0.0), 26.0);
        col += vec3(spec) * 0.35;

        // The hot centre. Without it the ball is saturated but dead — this is
        // the part that reads as lit from within rather than painted.
        col += vec3(1.0) * pow(max(0.0, 1.0 - r * 1.55), 3.0) * 0.55;

        // Antialias the rim in the alpha, not the colour: fading to white
        // would leave a pale halo on a dark page.
        float alpha = smoothstep(1.0, 0.975, r) * uAlpha;
        gl_FragColor = vec4(col, alpha);
    }
`;

const Ball: React.FC<{ colors: string[]; still: boolean; alpha: number }> = ({ colors, still, alpha }) => {
    const ref = useRef<THREE.ShaderMaterial>(null);
    const size = useThree((state) => state.size);

    const uniforms = useMemo(() => {
        // Padded rather than counted: a loop bound by a uniform is not
        // portable in GLSL ES 1.0, and repeating a colour costs nothing.
        const padded = Array.from({ length: LOBES }, (_, i) => new THREE.Color(colors[i % colors.length]));
        return {
            uTime: { value: 0 },
            uColors: { value: padded },
            uAlpha: { value: alpha },
            uAspect: { value: 1 },
        };
    }, [colors, alpha]);

    useFrame((_, delta) => {
        if (!ref.current) return;
        ref.current.uniforms.uAspect.value = size.height > 0 ? size.width / size.height : 1;
        if (!still) ref.current.uniforms.uTime.value += delta;
    });

    return (
        <ScreenQuad>
            <shaderMaterial
                ref={ref}
                uniforms={uniforms}
                vertexShader={VERTEX}
                fragmentShader={FRAGMENT}
                transparent
                depthWrite={false}
            />
        </ScreenQuad>
    );
};

export const BrandCore: React.FC<{
    /** The brand's colours, in order. Falls back to an unread palette. */
    colors?: string[];
    /** Dimmed in dark mode by the caller, since the page ground changes. */
    alpha?: number;
    className?: string;
    style?: React.CSSProperties;
}> = ({ colors, alpha = 1, className, style }) => {
    const palette = useMemo(() => {
        const hexes = (colors ?? [])
            .map((c) => c.trim())
            .filter((c) => /^#[0-9a-fA-F]{6}$/.test(c));
        return hexes.length ? hexes.slice(0, LOBES) : UNREAD;
    }, [colors]);

    // A still ball for anyone who asked for less motion. Rendered once and
    // left there rather than hidden: the sphere is the page's subject, and
    // removing it would leave a hole where the brand should be.
    const still = typeof window !== 'undefined'
        && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    return (
        <div
            aria-hidden
            data-testid="brand-core"
            className={cn('pointer-events-none', className)}
            style={style}
        >
            <Canvas
                // No more than 2×: this is a soft gradient with no detail to
                // resolve, and a 3× retina canvas costs fill rate for nothing.
                dpr={[1, 2]}
                gl={{ alpha: true, antialias: true }}
                // `demand` would need an invalidate on every frame of an
                // animation that never stops, which is the default loop with
                // extra steps. Frozen motion stops the clock instead.
                frameloop={still ? 'demand' : 'always'}
                style={{ background: 'transparent' }}
            >
                <Ball colors={palette} still={still} alpha={alpha} />
            </Canvas>
        </div>
    );
};
